import { useState, useEffect } from 'react'
import { Plus, Clock, CheckSquare, AlertTriangle } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useRealtime } from '../../context/RealtimeContext'
import { getEmployeesForFounder } from '../../services/auth.service'
import { createTask } from '../../services/tasks.service'
import { useToast } from '../../context/ToastContext'
import type { User } from '../../types/auth.types'
import type { Task } from '../../types'

function statusColor(status: string) {
  switch (status) {
    case 'done':        return '#e8a000'
    case 'in-progress': return '#ffc600'
    case 'review':      return '#ff8c00'
    default:            return '#555'
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'todo':        return 'To Do'
    case 'in-progress': return 'In Progress'
    case 'review':      return 'In Review'
    case 'done':        return 'Done'
    default:            return status
  }
}

function EmployeeCard({ employee, tasks }: { employee: User; tasks: Task[] }) {
  const empTasks    = tasks.filter(t => t.assignee === employee.name)
  const activeTasks = empTasks.filter(t => t.status === 'in-progress')
  const overdue     = empTasks.filter(t => t.status !== 'done' && new Date(t.dueDate) < new Date())
  const done        = empTasks.filter(t => t.status === 'done')

  const activityStatus = overdue.length > 0 ? 'overdue'
    : activeTasks.length > 0 ? 'active' : 'idle'

  const dotColor = activityStatus === 'overdue' ? '#cc3300'
    : activityStatus === 'active' ? '#e8a000' : '#555'

  return (
    <div className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
          style={{ background: '#ffc600', color: '#000' }}>
          {employee.avatarInitials}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-white">{employee.name}</p>
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
          </div>
          <p className="text-xs" style={{ color: '#a0a0a0' }}>{employee.email}</p>
        </div>
        <span className="text-xs px-2 py-0.5 rounded capitalize"
          style={{ background: `${dotColor}18`, color: dotColor }}>
          {activityStatus}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: 'Active', value: activeTasks.length, icon: Clock, color: '#ffc600' },
          { label: 'Done',   value: done.length,        icon: CheckSquare, color: '#e8a000' },
          { label: 'Overdue', value: overdue.length,    icon: AlertTriangle, color: overdue.length > 0 ? '#cc3300' : '#555' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-lg p-2 text-center" style={{ background: '#1a1a1a' }}>
            <Icon size={12} className="mx-auto mb-1" style={{ color }} />
            <p className="text-sm font-bold text-white">{value}</p>
            <p className="text-xs" style={{ color: '#555', fontSize: '10px' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Current task */}
      {activeTasks.length > 0 ? (
        <div className="rounded-lg p-3" style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.08)' }}>
          <p className="text-xs mb-0.5" style={{ color: '#555' }}>CURRENT TASK</p>
          <p className="text-sm text-white font-medium truncate">{activeTasks[0].title}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', fontSize: '10px' }}>
              {statusLabel(activeTasks[0].status)}
            </span>
            <span className="text-xs" style={{ color: '#555' }}>
              Due {new Date(activeTasks[0].dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>
      ) : (
        <div className="rounded-lg p-3 text-center" style={{ background: '#1a1a1a' }}>
          <p className="text-xs" style={{ color: '#333' }}>No active tasks</p>
        </div>
      )}
    </div>
  )
}

interface AssignForm {
  title: string; description: string; priority: Task['priority']; dueDate: string; assigneeId: string
}
const EMPTY_FORM: AssignForm = { title: '', description: '', priority: 'medium', dueDate: '', assigneeId: '' }

export default function FounderTeam() {
  const { currentUser } = useAuth()
  const { tasks, addTask } = useRealtime()
  const { toast } = useToast()
  const [team,      setTeam]      = useState<User[]>([])
  const [showForm,  setShowForm]  = useState(false)
  const [form,      setForm]      = useState<AssignForm>(EMPTY_FORM)
  const [err,       setErr]       = useState<string | null>(null)
  const [assigning, setAssigning] = useState(false)

  useEffect(() => {
    if (currentUser?.id) {
      getEmployeesForFounder(currentUser.id).then(setTeam).catch(console.error)
    }
  }, [currentUser?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const assignTask = async () => {
    if (!form.title.trim() || !form.assigneeId || !form.dueDate) {
      setErr('Title, assignee, and due date are required.'); return
    }
    const assignee = team.find(t => t.id === form.assigneeId)
    if (!assignee) return

    setAssigning(true)
    try {
      const saved = await createTask({
        title:       form.title.trim(),
        description: form.description.trim(),
        priority:    form.priority,
        status:      'todo',
        assignee:    assignee.name,
        dueDate:     form.dueDate,
        tags:        [],
      })
      addTask(saved)
      toast(`Task assigned to ${assignee.name} ✓`, 'success')
      setForm(EMPTY_FORM); setShowForm(false); setErr(null)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Failed to assign task')
    } finally {
      setAssigning(false)
    }
  }

  const inputStyle: React.CSSProperties = { width: '100%', background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, fontFamily: 'DM Sans, sans-serif', outline: 'none' }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>My Team</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{team.length} member{team.length !== 1 ? 's' : ''} reporting to you</p>
        </div>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: '#ffc600', color: '#000', boxShadow: '0 0 16px rgba(255,198,0,0.2)' }}>
          <Plus size={15} /> Assign Task
        </button>
      </div>

      {/* Task assignment form */}
      {showForm && (
        <div className="rounded-xl p-5 mb-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
          <h2 className="text-sm font-bold text-white mb-4">Assign New Task</h2>
          {err && <p className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: 'rgba(204,51,0,0.1)', color: '#cc3300' }}>{err}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>TASK TITLE</label>
              <input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="What needs to be done?" />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>ASSIGNEE</label>
              <select style={inputStyle} value={form.assigneeId} onChange={e => setForm(f => ({ ...f, assigneeId: e.target.value }))}>
                <option value="">Select team member…</option>
                {team.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>PRIORITY</label>
              <select style={inputStyle} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as Task['priority'] }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>DUE DATE</label>
              <input type="date" style={inputStyle} value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold block mb-1" style={{ color: '#a0a0a0' }}>DESCRIPTION</label>
              <textarea style={{ ...inputStyle, resize: 'none' }} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Additional context…" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setShowForm(false); setErr(null) }} className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ border: '1px solid rgba(255,198,0,0.25)', color: '#ffc600', background: 'transparent' }}>Cancel</button>
            <button onClick={assignTask} disabled={assigning} className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
              style={{ background: assigning ? 'rgba(255,198,0,0.5)' : '#ffc600', color: '#000' }}>
              {assigning && <span className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin inline-block" />}
              {assigning ? 'Assigning…' : 'Assign Task'}
            </button>
          </div>
        </div>
      )}

      {/* Employee cards */}
      {team.length === 0 ? (
        <div className="text-center py-16" style={{ color: '#333' }}>No employees assigned to you yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {team.map(emp => <EmployeeCard key={emp.id} employee={emp} tasks={tasks} />)}
        </div>
      )}
    </div>
  )
}
